import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useLanguage } from '@hooks';
import { parseTags, formatDate } from '@utils/api';

const StyledBlogCard = styled.div`
  position: relative;
  cursor: default;
  transition: var(--transition);
  height: 100%;
  width: 100%; /* 确保卡片占满容器宽度 */

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      transform: translateY(-7px);
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    position: relative;
    height: 480px; /* 固定高度确保一致性 */
    width: 100%; /* 确保内部容器占满宽度 */
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    overflow: hidden;
  }

  .project-cover {
    width: 100%;
    height: 180px; /* 固定封面图高度 */
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: var(--transition);

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .project-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 1.5rem;
    min-height: 0; /* 允许内容区域收缩 */
    width: 100%; /* 确保内容区域占满宽度 */
  }

  .project-date {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 500;
    margin-bottom: 12px;
    flex-shrink: 0;
  }

  .project-title {
    margin: 0 0 12px 0;
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
    font-weight: 600;
    line-height: 1.3;
    flex-shrink: 0;
    width: 100%; /* 确保标题占满宽度 */

    /* 限制标题最多显示2行 */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2.6em; /* 固定最小高度，确保一致性 */
    max-height: 2.6em; /* 约2行的高度 */

    a {
      position: static;
      color: inherit;
      text-decoration: none;
      display: block;
      width: 100%;

      &:hover {
        color: var(--green);
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  .project-description {
    color: var(--light-slate);
    font-size: var(--fz-sm);
    line-height: 1.4;
    flex: 1;
    width: 100%; /* 确保描述占满宽度 */

    /* 限制描述最多显示3行 */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 4.2em; /* 固定最小高度，确保一致性 */
    max-height: 4.2em; /* 约3行的高度 */

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-footer {
    margin-top: auto;
    padding-top: 1rem;
    flex-shrink: 0;
    width: 100%; /* 确保底部区域占满宽度 */
  }

  .project-tech-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 0;
    margin: 0 0 1rem 0;
    list-style: none;
    min-height: 1.5rem; /* 确保即使没有标签也有固定高度 */
    width: 100%; /* 确保标签列表占满宽度 */

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      color: var(--green);
      background-color: rgba(100, 255, 218, 0.1);
      border: 1px solid var(--green);
      border-radius: 3px;
      padding: 2px 6px;
      margin-right: 8px;
      margin-bottom: 4px;
      white-space: nowrap;

      /* 限制标签数量，超出部分隐藏 */
      &:nth-child(n + 4) {
        display: none;
      }
    }
  }

  .read-now-btn {
    ${({ theme }) => theme.mixins.smallButton};
    align-self: flex-start;
    flex-shrink: 0;
    font-size: var(--fz-xs);
    padding: 8px 16px;

    &:hover {
      background-color: rgba(100, 255, 218, 0.1);
      border-color: var(--green);
    }
  }
`;

/**
 * 博客卡片组件
 * @param {Object} post - 文章数据
 * @param {Function} onReadNow - 点击 Read Now 的回调函数
 */
const BlogCard = ({ post, onReadNow }) => {
  const { t, currentLanguage } = useLanguage();
  const { id, title, description, cover_image, tags, created_at, excerpt } = post;
  const tagList = parseTags(tags);

  // 语言环境映射
  const getLocaleFromLanguage = language => {
    const localeMap = {
      en: 'en-US',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW',
    };
    return localeMap[language] || 'en-US';
  };

  const formattedDate = formatDate(created_at, getLocaleFromLanguage(currentLanguage));

  const handleReadNow = e => {
    e.preventDefault();
    if (onReadNow) {
      onReadNow(post);
    }
  };

  return (
    <StyledBlogCard>
      <div className="project-inner">
        {cover_image && (
          <div className="project-cover">
            <img src={cover_image} alt={title} loading="lazy" />
          </div>
        )}

        <div className="project-content">
          <div className="project-date">{formattedDate}</div>

          <h3 className="project-title">
            <a href={`#post-${id}`} onClick={handleReadNow}>
              {title}
            </a>
          </h3>

          <div className="project-description">{excerpt || description}</div>

          <div className="project-footer">
            {tagList.length > 0 && (
              <ul className="project-tech-list">
                {tagList.slice(0, 3).map((tag, i) => (
                  <li key={i}>{tag}</li>
                ))}
              </ul>
            )}

            <button className="read-now-btn" onClick={handleReadNow}>
              {t('readNow')}
            </button>
          </div>
        </div>
      </div>
    </StyledBlogCard>
  );
};

BlogCard.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    cover_image: PropTypes.string,
    tags: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
  }).isRequired,
  onReadNow: PropTypes.func,
};

export default BlogCard;
